import * as THREE from 'three';
import type { FarBody } from './bodies';
import { MOON_COL_URI, MOON_HGT_URI, MOON_COL_MEAN, MOON_HGT_KM } from './moonAssets';
import { SKY_TRANS_GLSL } from './sky';
import { TILES_GLSL, tileUniforms, worldTiles } from './worldTiles';

// The far shell: how everything too far to be geometry is painted (observer-centric LOD, step 7).
//
// The galaxy, the stars and the moon used to be painted on a dome that follows the eye, in the directions the server gave for the world. That is right for a viewer on the ground and
// wrong for any other: the moon seen from 10 km up from the other side of the planet is not where the ground's sky has it, and a planet seen from orbit is not a point at all.
//
// This is the general mechanism: ONE full-screen pass, drawn right after the sky pass and before the scene, that traces every registered body (bodies.ts) as an analytic sphere from the
// eye. A body's place, size and the light on it come from the registry relative to the observer (in doubles, on the CPU), so a moon from a planet's ground, the same moon from half way,
// the planet from the moon and a second world nobody has told this file about are all the same few lines. Bodies are composited nearest first. The pass has no depth: the scene's
// own things (the sea, the land, the cloud) are drawn over it where they exist, and the stars and the galaxy, drawn after, are cut by the bodies' spheres themselves (occluders, below).
//
// Two kinds of body are painted:
//   rock   an airless sphere with a surface map, lit by the star with the Lommel-Seeliger law (a full moon is flat, not limb-darkened). Its brightness is the one the server's moon beam
//          gives (flux * albedo * 2/3 * the Lambert sphere's phase function = the disc's mean), so a moon seen from the ground has the beam's strength and one seen from anywhere
//          else keeps the surface brightness it has (which does not depend on the distance). The home air in front of it dims and reddens it (the same exp(-tau * air mass) the light
//          field and the stars use, worked along the actual ray, so it is right from a mountain top and from orbit).
//   world  a planet's surface: an ocean with its sun glint, seen through the air and under the cloud. The air's own light is not painted here: it is the sky pass's (sky.ts), one table of the
//          radiance along every direction for the observer wherever it is, so the limb, the haze under the horizon and the blue overhead are the same thing seen from different heights; this
//          multiplies the surface by the sun's transmittance (the exponential column, for now) and the view's (that table's). THE OCEAN IS A STUB (a colour matched to the near sea's deep water
//          and the sun's glint). The cloud is not painted here: it is the world painter's, a pass of its own after the sea (worldCloud.ts), because the sea is opaque to the horizon and would
//          hide it; this pass only dims the ocean by the cloud's cell-mean transmittance from the same tiles, so a glint does not shine through an overcast.
// The sun's disc and glare stay the sky's own drawings (sunOrb, sunGlare): the registry only tells them where the star is and how much of it a body hides (a fade).
//
// Brightness is in the sky's units (irradiance-equivalent, the same as the sky, the stars and the moon were) through the same curve, 1 - exp(-SKY_EXPOSURE * gain * E): the pass is
// blended onto what is there as a screen (dst + src (1 - dst)), as the moon was, so the air's own light in front of a body adds to it.

const SKY_EXPOSURE = 6.0;
const DISC_X_MAX = 1.8;          // cap on the curve's argument at the lit centre of a rock's disc (moon.ts)
export const FAR_MAX_BODIES = 4;
const COL_W = 2048;              // the colour map's width
const HGT_W = 1024, HGT_H = 512;
export const FAR_RELIEF = 3.0;   // strength of the relief: 1 is the elevation map's true slopes
const MOON_CLOUD_LO = 0.30, MOON_CLOUD_HI = 0.90;
const SHELL_TOP_M = 100e3;       // the air is taken to end here
const H_RAY = 8000, H_MIE = 1200;

/**
 * The air's column along a ray, for the far shell's sunlit worlds (the sun's path from a surface point out through that world's air). The stars, the galaxy and the moon read the sky-view
 * table's transmittance instead (bodyAir.ts). Needs PI_ defined first.
 */
export const AIR_COLUMN_GLSL = /* glsl */ `
const float SHELL_TOP = ${SHELL_TOP_M.toFixed(1)};
const float HR = ${H_RAY.toFixed(1)};
const float HM = ${H_MIE.toFixed(1)};
// The air's column (vertical-equivalent air mass, Rayleigh and aerosol) along a ray from p (relative to the planet's centre) in direction dir, to the top of the air; 1000 where the ray
// meets the planet. Worked in the ray's own coordinates (the perpendicular distance from the centre and the distance along), so a point far from the planet loses nothing.
vec2 column(vec3 p, vec3 dir, float R, int n) {
  float Rt = R + SHELL_TOP;
  float b = dot(p, dir);
  vec3 cp = cross(p, dir);
  float perp2 = dot(cp, cp);
  if (b < 0.0 && perp2 < R * R) return vec2(1000.0);
  float d2 = Rt * Rt - perp2;
  if (d2 <= 0.0) return vec2(0.0);
  float sq = sqrt(d2);
  float tA = max(-b - sq, 0.0), tB = -b + sq;
  if (tB <= 0.0) return vec2(0.0);
  float L = tB - tA;
  vec2 sum = vec2(0.0);
  for (int k = 0; k < 16; k++) {
    if (k >= n) break;
    float u0 = float(k) / float(n), u1 = float(k + 1) / float(n);
    float f0 = 0.5 - 0.5 * cos(PI_ * u0), f1 = 0.5 - 0.5 * cos(PI_ * u1);
    float s = tA + L * 0.5 * (f0 + f1), ds = L * (f1 - f0);
    float al = b + s;
    float h = sqrt(perp2 + al * al) - R;
    sum += vec2(exp(-h / HR) * ds / HR, exp(-h / HM) * ds / HM);
  }
  return sum;
}

`;

/** What the stars and the galaxy need to be hidden by a body: a world's sphere (hides them where the eye has no table of its own air, bodyAir.ts) and a second sphere (the moon). */
export const SPACE_GLSL = /* glsl */ `
uniform vec4  uHome;            // a world: centre from the eye (render axes, m), radius; w = 0 when there is none
uniform vec4  uOcc;             // another sphere that hides what is behind it (the moon); w = 0 when there is none
bool occludedBy(vec4 s, vec3 d) { return s.w > 0.0 && dot(s.xyz, d) > 0.0 && length(cross(s.xyz, d)) < s.w; }
`;


export const FAR_VERT = /* glsl */ `
uniform mat4 uInvProj;
varying vec3 vDir;
void main() {
  vec4 v = uInvProj * vec4(position.xy, 0.0, 1.0);          // (the far plane itself is at infinity for a huge far value: w comes out 0)
  vec3 vd = v.xyz / v.w;
  vDir = (vec4(vd, 0.0) * viewMatrix).xyz;               // view space to world axes (the rotation's transpose)
  gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const FRAG = /* glsl */ `
precision highp float;
#define NB ${FAR_MAX_BODIES}
uniform int   uN;
uniform vec4  uC[NB];            // centre (m, render axes, from the eye), radius
uniform vec4  uL[NB];            // toward the star (unit), kind (1 rock, 2 world)
uniform vec4  uF[NB];            // flux per channel, mean Lommel-Seeliger shade at this phase
uniform vec4  uM[NB];            // disc-mean surface brightness per channel (rock)
uniform vec3  uFx[3 * NB];       // body-fixed frame rows (render axes)
uniform int   uHome;             // index of the world the air belongs to, -1 if none
${SKY_TRANS_GLSL}
uniform vec4  uAir;              // zenith optical depth of the clean air per channel (rgb), aerosol (w)
uniform float uWorldA;           // how much of the world's surface (its ocean; the cloud is worldCloud.ts's own pass) shows: the near field covers it where it is whole, and grows transparent over it as it fades out
uniform float uSVOn;             // 1 when the eye is the home planet's observer: the sky pass paints the air's own light, and the view's transmittance to the surface is the table's
uniform float uEGain;            // SKY_EXPOSURE * the display exposure gain
uniform float uSrgbOut;
uniform float uPixAng;           // angle of one pixel (rad)
uniform samplerCube uCloudT;
uniform float uCloudOn;
uniform sampler2D uCol;
uniform sampler2D uHgt;
uniform float uTexOn;
uniform float uColMean;
uniform float uSlope;
uniform vec2  uHTex;
uniform float uSea;              // sea sun-glint roughness (variance of the slope)
uniform int   uDbg;              // a test switch: 1 shows the ray, 2 the first body's coverage, 3 its colour before the exposure
varying vec3 vDir;
const float PI_ = 3.14159265358979;
${AIR_COLUMN_GLSL}
${TILES_GLSL}
uniform float uCloudsOn;         // 1 once the world painter's global tier is in: the ocean is dimmed by its cloud
// The share of the light that gets through the cloud over a point (q: its coordinates in the patch's frame), from the cell-mean cover of each deck and what its cloud does to light
// (the world's cloud tables): x the sun's beam on its way down (by the sun's cosine against the sea, cosL), y the sea's light on its way up to the eye (the diffuse reflectance).
vec2 cloudCellT(vec3 q, float foot, float cosL) {
  vec2 T = vec2(1.0); float ln;
  float lod = max(log2(foot / uTP.z), 0.0);
  for (int k = 0; k < 3; k++) {
    vec4 v = fieldAt(toBody(q), k, lod, ln);
    float cov = clamp(v.x, 0.0, 1.0);
    vec4 r = deckResp(k, cov, v.y, cosL);
    T *= 1.0 - cov * vec2(dot(r.rgb, vec3(0.2126, 0.7152, 0.0722)), r.a);
  }
  return T;
}

float hash(vec3 p) { return fract(sin(dot(p, vec3(127.1, 311.7, 74.7))) * 43758.5453); }
float vnoise(vec3 p) {
  vec3 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = mix(mix(hash(i), hash(i + vec3(1,0,0)), f.x), mix(hash(i + vec3(0,1,0)), hash(i + vec3(1,1,0)), f.x), f.y);
  float b = mix(mix(hash(i + vec3(0,0,1)), hash(i + vec3(1,0,1)), f.x), mix(hash(i + vec3(0,1,1)), hash(i + vec3(1,1,1)), f.x), f.y);
  return mix(a, b, f.z);
}
vec3 encodeSrgb(vec3 c) { return mix(c * 12.92, 1.055 * pow(max(c, vec3(0.0)), vec3(1.0 / 2.4)) - 0.055, step(0.0031308, c)); }

// The sphere: perpendicular distance of the centre from the ray, and where the ray meets it (< 0: it does not, or the sphere is behind)
float hitSphere(vec3 c, float r, vec3 d, out float perp, out float tca) {
  tca = dot(c, d);
  vec3 cp = cross(c, d);
  perp = length(cp);
  if (tca <= 0.0 || perp >= r) return -1.0;
  return tca - sqrt(r * r - perp * perp);
}

// A rock: returns (display colour, coverage)
vec4 paintRock(int i, vec3 d) {
  vec3 c = uC[i].xyz; float R = uC[i].w;
  float perp, tca;
  float t = hitSphere(c, R, d, perp, tca);
  float edge = uPixAng * tca;                                 // a pixel's width at the sphere's distance
  float cov = clamp(0.5 + (R - perp) / max(edge, 1.0), 0.0, 1.0);
  if (tca <= 0.0 || cov <= 0.0) return vec4(0.0);
  if (t < 0.0) t = tca - sqrt(max(R * R - min(perp, R) * min(perp, R), 0.0));
  vec3 p = d * t - c;
  vec3 n = normalize(p);                                      // the surface normal, outward
  vec3 L = uL[i].xyz;
  float ce = max(dot(n, -d), 0.0);
  vec3 nS = n;
  vec3 alb = vec3(1.0);
  float proc = 1.0;
  if (uTexOn > 0.5) {
    vec3 b = vec3(dot(uFx[3 * i], n), dot(uFx[3 * i + 1], n), dot(uFx[3 * i + 2], n));      // body frame: x right, y north, z toward the near side
    float lon = atan(b.x, b.z), lat = asin(clamp(b.y, -1.0, 1.0));
    vec2 tuv = vec2(lon / (2.0 * PI_) + 0.5, lat / PI_ + 0.5);
    float foot = uPixAng * t / max(ce, 0.08);                 // a pixel's footprint on the surface (m)
    float lodC = log2(max(foot / (2.0 * PI_ * R / ${COL_W}.0), 1.0));
    float lodH = log2(max(foot / (2.0 * PI_ * R / ${HGT_W}.0), 1.0));
    alb = textureLod(uCol, tuv, lodC).rgb / uColMean;
    proc = 0.0;
    float dE = textureLod(uHgt, tuv + vec2(uHTex.x, 0.0), lodH).r - textureLod(uHgt, tuv - vec2(uHTex.x, 0.0), lodH).r;
    float dN = textureLod(uHgt, tuv + vec2(0.0, uHTex.y), lodH).r - textureLod(uHgt, tuv - vec2(0.0, uHTex.y), lodH).r;
    vec3 north0 = uFx[3 * i + 1];
    vec3 east = normalize(cross(north0, n) + vec3(1e-6)), north = cross(n, east);
    n = normalize(n - uSlope * (dE / max(cos(lat), 0.15) * east + dN * north));
  }
  float ci = dot(n, L);
  float lit = smoothstep(-0.04, 0.08, dot(nS, L));            // the geometric terminator: relief cannot light the night side
  float shade = ci > 0.0 ? lit * ci / (ci + ce) : 0.0;        // Lommel-Seeliger
  float maria = mix(1.0, 0.72 + 0.28 * smoothstep(0.35, 0.65, 0.65 * vnoise(nS * 2.3 + 4.0) + 0.35 * vnoise(nS * 5.1 + 9.0)), proc);
  vec3 k = shade * maria * alb / max(uF[i].w, 1e-3);          // 1 = the disc's average brightness
  // the home air between the eye and the rock dims and reddens it: the table's transmittance along this direction, from the eye where it is (the rock is a body at infinity to the air)
  vec3 tAir = (uSVOn > 0.5 && uHome >= 0) ? svTrans(d) : vec3(1.0);
  vec3 x = min(uEGain * uM[i].xyz * tAir, vec3(${DISC_X_MAX.toFixed(2)})) * k;
  float ct = smoothstep(${MOON_CLOUD_LO.toFixed(2)}, ${MOON_CLOUD_HI.toFixed(2)}, textureCube(uCloudT, d).a);
  x *= mix(1.0, ct * ct, uCloudOn);                           // cloud in front of the rock, in its own direction
  return vec4(1.0 - exp(-x), cov);
}

// A world: (display colour, coverage). The air's own light is the sky pass's (sky.ts: the sky-view table holds the inscatter along every direction, including the ones that end on the
// surface); this paints the surface seen through that air: the ocean, its glint and the cloud, times the sun's and the view's transmittance.
vec4 paintWorld(int i, vec3 d) {
  vec3 c = uC[i].xyz; float R = uC[i].w;
  vec3 o = -c;                                                // the eye, from the centre
  float b = dot(o, d), perp2 = dot(cross(o, d), cross(o, d));
  float s2 = R * R - perp2;
  if (s2 <= 0.0) return vec4(0.0);
  float tS = -b - sqrt(s2);
  if (tS <= 0.0) return vec4(0.0);                            // (looking up from the ground the surface is behind)
  vec3 L = uL[i].xyz, F = uF[i].xyz;
  vec3 p = o + d * tS;
  vec3 n = normalize(p);
  float cosL = dot(n, L), cosV = max(dot(n, -d), 0.02);
  vec2 cs = column(p, L, R, 8);
  vec3 Ts = exp(-(uAir.rgb * cs.x + vec3(uAir.w) * cs.y));
  vec3 Tv = (uSVOn > 0.5 && i == uHome) ? svTrans(d) : vec3(1.0);
  // the ocean: its body colour under the sun, and the sun's glint (Cox and Munk's slope distribution, Fresnel at the facet)
  vec3 q = vec3(dot(uFx[3 * i], n), dot(uFx[3 * i + 1], n), dot(uFx[3 * i + 2], n));
  vec3 sea = vec3(0.001, 0.0155, 0.066) * F * max(cosL, 0.0);        // matched to the near sea's deep water seen from straight above under a sun 40 degrees up, through the display curve (a stub: see the note at the top)
  vec3 hv = normalize(L - d);
  float cn = max(dot(n, hv), 1e-3), tn2 = (1.0 - cn * cn) / (cn * cn);
  float fr = 0.02 + 0.98 * pow(1.0 - max(dot(-d, hv), 0.0), 5.0);
  float glint = fr * exp(-tn2 / uSea) / (4.0 * uSea * cn * cn * cn * cn * cosV);
  sea += F * glint * step(0.0, cosL);
  // the cloud over it dims it (the cloud itself is worldCloud.ts's): the share of the light that gets through, once for the sun's way down and once for the view's way up
  vec2 Tc = uCloudsOn > 0.5 ? cloudCellT(q, uPixAng * tS, cosL) : vec2(1.0);
  vec3 U = uWorldA * Ts * Tv * sea * (Tc.x * Tc.y) * step(-0.3, cosL);
  return vec4(1.0 - exp(-uEGain * U), 1.0);
}

// Whether the ray meets the body's solid sphere (a planet that is not painted still hides what is behind it).
float sphereCov(int i, vec3 d) {
  vec3 o = -uC[i].xyz; float R = uC[i].w;
  float b = dot(o, d), s2 = R * R - (dot(o, o) - b * b);
  return (s2 > 0.0 && (-b - sqrt(s2)) > 0.0) ? 1.0 : 0.0;
}

void main() {
  vec3 d = normalize(vDir);
  if (uDbg == 1) { gl_FragColor = vec4(d * 0.5 + 0.5, 1.0); return; }
  if (uDbg == 9) { gl_FragColor = vec4(1.0, 0.0, 0.0, 1.0); return; }
  vec3 acc = vec3(0.0);
  vec3 trans = vec3(1.0);
  for (int i = 0; i < NB; i++) {
    if (i >= uN) break;
    float kind = uL[i].w;
    vec4 r = kind < 1.5 ? paintRock(i, d) : uWorldA > 0.0 ? paintWorld(i, d) : vec4(0.0, 0.0, 0.0, sphereCov(i, d));   // (on the ground the near field has the world: only its solid is needed)
    if (uDbg == 2 + i) { gl_FragColor = vec4(r.rgb, 1.0) + vec4(0.0, 0.0, r.a * 0.5, 0.0); return; }
    acc += trans * r.rgb;
    trans *= (1.0 - r.a);
  }
  if (dot(acc, vec3(1.0)) <= 0.0) discard;
  if (uSrgbOut > 0.5) acc = encodeSrgb(acc);
  gl_FragColor = vec4(acc, 1.0);
}
`;

/** The maps of the moon (embedded), decoded once. */
export interface MoonMaps { col: THREE.Texture; hgt: THREE.Texture; }
let mapsPromise: Promise<MoonMaps> | null = null;
export function loadMoonMaps(): Promise<MoonMaps> {
  if (mapsPromise) return mapsPromise;
  mapsPromise = new Promise<MoonMaps>((resolve, reject) => {
    if (typeof Image === 'undefined') { reject(new Error('no Image')); return; }
    const load = (uri: string, srgb: boolean): Promise<THREE.Texture> => new Promise((res, rej) => {
      const img = new Image();
      img.onload = () => {
        const t = new THREE.Texture(img);
        t.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
        t.generateMipmaps = true; t.minFilter = THREE.LinearMipmapLinearFilter; t.magFilter = THREE.LinearFilter;
        t.wrapS = THREE.RepeatWrapping; t.wrapT = THREE.ClampToEdgeWrapping;        // the seam at the far side's meridian is filtered across, not edged
        t.needsUpdate = true;
        res(t);
      };
      img.onerror = () => rej(new Error('map failed'));
      img.src = uri;
    });
    Promise.all([load(MOON_COL_URI, true), load(MOON_HGT_URI, false)]).then(([col, hgt]) => resolve({ col, hgt }), reject);
  });
  return mapsPromise;
}

export interface FarShellFrame {
  bodies: FarBody[];                                    // nearest first
  /** The home world's index in `bodies` (the one whose air the eye is in or looks through), or -1. */
  home: number;
  /** Zenith optical depth of the clean air per channel, and the aerosol's (grey). */
  air: [number, number, number, number];
  /** Whether the world's surface (its ocean) is painted at all (0: the near field covers all of it; 1: part of the near field is giving way or gone). */
  worldA: number;
  /** The sky-view table's transmittance texture and the horizon it is laid out for, when the eye is the home planet's observer (the air in front of the planet's surface); null otherwise. */
  skyTrans: THREE.Texture | null;
  skyElH: number;
  eGain: number;
  srgbOut: boolean;
  cloudProbe: THREE.Texture | null;
}

const _v = new THREE.Vector4();

export class FarShell {
  readonly mesh: THREE.Mesh;
  private geo: THREE.BufferGeometry;
  private mat: THREE.ShaderMaterial;
  private uC: THREE.Vector4[] = []; private uL: THREE.Vector4[] = []; private uF: THREE.Vector4[] = []; private uM: THREE.Vector4[] = [];
  private uFx: THREE.Vector3[] = [];
  private srgb = true;
  private cloudTex: THREE.Texture | null = null;

  constructor() {
    for (let i = 0; i < FAR_MAX_BODIES; i++) { this.uC.push(new THREE.Vector4()); this.uL.push(new THREE.Vector4(0, 1, 0, 1)); this.uF.push(new THREE.Vector4()); this.uM.push(new THREE.Vector4()); }
    for (let i = 0; i < 3 * FAR_MAX_BODIES; i++) this.uFx.push(new THREE.Vector3());
    this.geo = new THREE.BufferGeometry();
    this.geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array([-1, -1, 0, 3, -1, 0, -1, 3, 0]), 3));
    this.mat = new THREE.ShaderMaterial({
      vertexShader: FAR_VERT, fragmentShader: FRAG,
      transparent: true, depthTest: false, depthWrite: false, toneMapped: false, fog: false,
      blending: THREE.CustomBlending, blendEquation: THREE.AddEquation, blendSrc: THREE.OneFactor, blendDst: THREE.OneMinusSrcColorFactor,
      blendSrcAlpha: THREE.ZeroFactor, blendDstAlpha: THREE.OneFactor,       // (the alpha of a target is not touched: in the sky's probe it is the cloud's transmittance)
      uniforms: {
        uInvProj: { value: new THREE.Matrix4() },
        uN: { value: 0 }, uC: { value: this.uC }, uL: { value: this.uL }, uF: { value: this.uF }, uM: { value: this.uM }, uFx: { value: this.uFx },
        uHome: { value: -1 }, uAir: { value: new THREE.Vector4(0.05, 0.1, 0.22, 0.03) }, uWorldA: { value: 0 }, uSVOn: { value: 0 }, uSVT: { value: null as THREE.Texture | null }, uElH: { value: 0 }, uCloudsOn: { value: 0 },
        ...tileUniforms(),
        uEGain: { value: SKY_EXPOSURE }, uSrgbOut: { value: 1 }, uPixAng: { value: 0.001 },
        uCloudT: { value: null as THREE.Texture | null }, uCloudOn: { value: 0 },
        uCol: { value: null as THREE.Texture | null }, uHgt: { value: null as THREE.Texture | null }, uTexOn: { value: 0 }, uColMean: { value: MOON_COL_MEAN },
        uSlope: { value: FAR_RELIEF * MOON_HGT_KM / (2 * (1737.4 * Math.PI / HGT_H)) }, uHTex: { value: new THREE.Vector2(1 / HGT_W, 1 / HGT_H) },
        uSea: { value: 0.04 }, uDbg: { value: 0 },
      },
    });
    this.mesh = new THREE.Mesh(this.geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -900;                       // after the sky pass (-1000) and the horizon cutter (-10 is later still: it only writes depth), before the scene
    this.mesh.visible = false;
    this.mesh.onBeforeRender = (r, _s, camera) => {
      (this.mat.uniforms.uInvProj.value as THREE.Matrix4).copy(camera.projectionMatrixInverse);
      // drawn into a target the output is plain display values
      const toTarget = r.getRenderTarget() !== null;
      this.mat.uniforms.uSrgbOut.value = toTarget ? 0 : (this.srgb ? 1 : 0);
      this.mat.uniforms.uCloudT.value = toTarget ? null : this.cloudTex;       // (the probe is not read while it is being written)
    };
    void loadMoonMaps().then(m => {
      this.mat.uniforms.uCol.value = m.col; this.mat.uniforms.uHgt.value = m.hgt; this.mat.uniforms.uTexOn.value = 1;
    }).catch(() => { /* headless: the procedural patches stand in */ });
  }

  /** Per frame. `pixAng`: the angle one pixel spans (rad). */
  update(f: FarShellFrame, pixAng: number): void {
    const n = Math.min(f.bodies.length, FAR_MAX_BODIES);
    const u = this.mat.uniforms;
    u.uN.value = n;
    for (let i = 0; i < n; i++) {
      const b = f.bodies[i];
      this.uC[i].set(b.c[0], b.c[1], b.c[2], b.r);
      this.uL[i].set(b.lightDir[0], b.lightDir[1], b.lightDir[2], b.kind === 'world' ? 2 : 1);
      this.uF[i].set(b.flux[0], b.flux[1], b.flux[2], b.meanShade);
      this.uM[i].set(b.mean[0], b.mean[1], b.mean[2], 0);
      for (let k = 0; k < 3; k++) this.uFx[3 * i + k].set(b.fixed[3 * k], b.fixed[3 * k + 1], b.fixed[3 * k + 2]);
    }
    u.uHome.value = f.home;
    (u.uAir.value as THREE.Vector4).set(f.air[0], f.air[1], f.air[2], f.air[3]);
    u.uWorldA.value = f.worldA; u.uCloudsOn.value = worldTiles.haveGlobal ? 1 : 0; worldTiles.setUniforms(u);
    u.uSVOn.value = f.skyTrans ? 1 : 0; u.uSVT.value = f.skyTrans; u.uElH.value = f.skyElH;
    u.uEGain.value = f.eGain; this.srgb = f.srgbOut; u.uSrgbOut.value = f.srgbOut ? 1 : 0; u.uPixAng.value = pixAng;
    this.cloudTex = f.cloudProbe; u.uCloudT.value = f.cloudProbe; u.uCloudOn.value = f.cloudProbe ? 1 : 0;
    this.mesh.visible = n > 0;
  }

  dispose(): void { this.geo.dispose(); this.mat.dispose(); }
}

void _v;
